import React, { useState } from 'react';
import { Phone, Mail, Send, MapPin, MessageCircle, Check } from 'lucide-react';
import axios from 'axios';
import { API, imageUrl } from '../config';
import { useBlock } from './SiteContent';
import ScheduleWidget from './ScheduleWidget';
import { trackGoal } from '../lib/metrika';

const DEFAULT = {
  title: 'Записаться',
  lead: 'Просто напишите. Это и будет первым шагом на вашей дороге к себе.',
  phone: '+375 (29) 123-45-67',
  telegram: '@belavskaya_psy',
  email: 'hello@belavskaya.com',
  address: 'г. Минск, ул. Кирова, 8 · по записи',
};

const telHref = (s) => 'tel:' + (s || '').replace(/[^+\d]/g, '');
const tgHref = (s) => 'https://t.me/' + (s || '').replace(/^@/, '');
const mailHref = (s) => 'mailto:' + (s || '').trim();

export default function Contact() {
  const c = useBlock('contact', DEFAULT);
  const [form, setForm] = useState({ name: '', contact: '', preferred_time: '', message: '' });
  const [status, setStatus] = useState({ type: 'idle', msg: '' });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) {
      setStatus({ type: 'error', msg: 'Пожалуйста, заполните имя и контакт.' });
      return;
    }
    setStatus({ type: 'loading', msg: '' });
    try {
      await axios.post(`${API}/contact`, form);
      trackGoal('contact-sent');
      setStatus({ type: 'success', msg: 'Спасибо. Я свяжусь с вами в ближайшее время — обычно в течение дня.' });
      setForm({ name: '', contact: '', preferred_time: '', message: '' });
    } catch (err) {
      setStatus({ type: 'error', msg: 'Не получилось отправить. Напишите, пожалуйста, в Telegram.' });
    }
  };

  const inputClass = 'w-full bg-cream border border-ink/20 px-4 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-wine transition-colors';

  return (
    <section id="contact" className="py-16 md:py-24 bg-cream" data-testid="contact">
      <div className="max-w-6xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-10 md:gap-14">
        <div className="reveal">
          <div className="flex items-center gap-3 mb-4 text-[12px] tracking-[0.24em] uppercase text-wine/70">
            <span className="gold-divider" />
            <span data-testid="contact-eyebrow">{(c.title || DEFAULT.title).toLowerCase()}</span>
          </div>
          <h2 className="font-heading text-3xl md:text-[44px] text-ink leading-tight mb-4" data-testid="contact-heading">
            Если чувствуете,<br/>что это&nbsp;— ваше&nbsp;пространство.
          </h2>
          <p className="text-ink/65 mb-7 max-w-md text-[15px]" data-testid="contact-lead">
            {c.lead || DEFAULT.lead}
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
            <div>
              <label className="block text-xs text-ink/60 uppercase tracking-wider mb-1.5" htmlFor="name">Имя</label>
              <input id="name" name="name" type="text" value={form.name} onChange={onChange} placeholder="Как к вам обращаться" className={inputClass} data-testid="contact-input-name" />
            </div>
            <div>
              <label className="block text-xs text-ink/60 uppercase tracking-wider mb-1.5" htmlFor="contact">Контакт</label>
              <input id="contact" name="contact" type="text" value={form.contact} onChange={onChange} placeholder="+375... или @telegram" className={inputClass} data-testid="contact-input-contact" />
            </div>
            <div>
              <label className="block text-xs text-ink/60 uppercase tracking-wider mb-1.5" htmlFor="preferred_time">Удобное время</label>
              <input id="preferred_time" name="preferred_time" type="text" value={form.preferred_time} onChange={onChange} placeholder="например, вечер будних" className={inputClass} data-testid="contact-input-time" />
            </div>
            <div>
              <label className="block text-xs text-ink/60 uppercase tracking-wider mb-1.5" htmlFor="message">Пара слов о&nbsp;запросе (по&nbsp;желанию)</label>
              <textarea id="message" name="message" rows={4} value={form.message} onChange={onChange} placeholder="Коротко — этого достаточно" className={`${inputClass} resize-none`} data-testid="contact-input-message" />
            </div>

            <button
              type="submit" disabled={status.type === 'loading'}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-ink text-ink hover:bg-wine hover:text-cream hover:border-wine transition-colors duration-300 disabled:opacity-60"
              data-testid="contact-submit"
            >
              <Send size={16} />
              {status.type === 'loading' ? 'Отправляю...' : 'Отправить заявку'}
            </button>

            {status.type === 'success' && (
              <p className="flex items-start gap-2 text-wine bg-wine/5 border border-wine/20 px-4 py-3 mt-3 text-[14px]" data-testid="contact-success">
                <Check size={16} className="mt-0.5 shrink-0" />
                <span>{status.msg}</span>
              </p>
            )}
            {status.type === 'error' && (
              <p className="text-wine bg-wine/5 border border-wine/20 px-4 py-3 mt-3 text-[14px]" data-testid="contact-error">{status.msg}</p>
            )}
          </form>
        </div>

        <div className="reveal">
          <div className="relative aspect-[5/3] overflow-hidden mb-6 bg-oat">
            <img src={imageUrl('contact_decor')} alt="" loading="lazy" className="w-full h-full object-cover" data-testid="contact-decor-img" />
          </div>

          <div className="border border-ink/10 p-7">
            <h3 className="font-heading text-2xl text-ink mb-5">Прямые контакты</h3>
            <ul className="space-y-3">
              {(c.phone || DEFAULT.phone) && (
                <li className="flex items-start gap-3 min-h-[44px] py-1" data-testid="contact-phone-row">
                  <Phone size={16} className="text-wine mt-1.5 shrink-0" />
                  <div className="py-0.5">
                    <div className="text-xs text-ink/55 uppercase tracking-wider">Телефон</div>
                    <a href={telHref(c.phone || DEFAULT.phone)} onClick={() => trackGoal('phone-click')} className="text-ink hover:text-wine transition-colors block py-0.5">{c.phone || DEFAULT.phone}</a>
                  </div>
                </li>
              )}
              {(c.telegram || DEFAULT.telegram) && (
                <li className="flex items-start gap-3 min-h-[44px] py-1" data-testid="contact-telegram-row">
                  <MessageCircle size={16} className="text-wine mt-1.5 shrink-0" />
                  <div className="py-0.5">
                    <div className="text-xs text-ink/55 uppercase tracking-wider">Telegram</div>
                    <a href={tgHref(c.telegram || DEFAULT.telegram)} onClick={() => trackGoal('telegram-click')} className="text-ink hover:text-wine transition-colors block py-0.5">{c.telegram || DEFAULT.telegram}</a>
                  </div>
                </li>
              )}
              {(c.email || DEFAULT.email) && (
                <li className="flex items-start gap-3 min-h-[44px] py-1" data-testid="contact-email-row">
                  <Mail size={16} className="text-wine mt-1.5 shrink-0" />
                  <div className="py-0.5">
                    <div className="text-xs text-ink/55 uppercase tracking-wider">Email</div>
                    <a href={mailHref(c.email || DEFAULT.email)} onClick={() => trackGoal('email-click')} className="text-ink hover:text-wine transition-colors block py-0.5">{c.email || DEFAULT.email}</a>
                  </div>
                </li>
              )}
              {(c.address || DEFAULT.address) && (
                <li className="flex items-start gap-3 min-h-[44px] py-1" data-testid="contact-address-row">
                  <MapPin size={16} className="text-wine mt-1.5 shrink-0" />
                  <div className="py-0.5">
                    <div className="text-xs text-ink/55 uppercase tracking-wider">Кабинет</div>
                    <div className="text-ink py-0.5">{c.address || DEFAULT.address}</div>
                  </div>
                </li>
              )}
            </ul>
          </div>

          <div className="mt-5 border border-wine/30 bg-wine/[0.03] p-5">
            <div className="font-heading text-lg text-wine mb-1">Если сейчас тяжело</div>
            <p className="text-[14px] text-ink/75 leading-relaxed">
              Круглосуточный телефон доверия:&nbsp;
              <a href="tel:+375173524444" onClick={() => trackGoal('hotline-click')} className="font-medium text-ink hover:text-wine">8-017-352-44-44</a>. Бесплатно, анонимно, всегда.
            </p>
          </div>

          <ScheduleWidget />
        </div>
      </div>
    </section>
  );
}
